import { useEffect, useRef, useState } from 'react';
import { ApiFailure, api } from '../lib/api';

/**
 * 底图配置。
 *
 * ★ 底图原来只能靠构建期的 `VITE_MAP_TILE_URL` —— 换一次要重新构建前端
 *   再发一次版。而「换底图」通常发生在账单来了、或者这家在这个国家的图
 *   不准的时候,那时候没人想发版。
 *
 * ★ 这一屏必须说清楚一件和别处**不一样**的事:
 *   瓦片是**浏览器**去拉的,所以地址里的 key 一定会到客户端。
 *   所有瓦片服务都是这样,它们靠 HTTP Referer 白名单防盗用。
 *   不写这句话的话,人会以为存成 `env:` 引用就等于藏住了 key。
 */

interface Cfg {
  tileUrl: string | null;
  keyRef: string | null;
  attribution: string | null;
}

/**
 * 现成的底图。
 *
 * ★ 前三个是**同一家的三种样式**,按「花不花」排:
 *   配的人真正要选的不是供应商,是「地图看起来什么样」——
 *   而 OSM 那份公共瓦片颜色太满(绿地、建筑、林地全是实色),
 *   图钉压上去分不出来,而司机在阳光下看的就是这块屏。
 *
 * ★ 后面几个留着别的供应商:自建和换家这两件事迟早要发生,
 *   而那时候没人想从零拼一个地址。
 */
const PRESETS: Array<{
  name: string;
  url: string;
  attribution: string;
  note: string;
  needsKey: boolean;
}> = [
  {
    name: '街道(最像 Google)',
    url: 'https://api.maptiler.com/maps/streets-v2/style.json?key={key}',
    attribution: '© MapTiler © OpenStreetMap contributors',
    note: '路名清楚、颜色克制,和 Google 地图最接近',
    needsKey: true,
  },
  {
    name: '简洁',
    url: 'https://api.maptiler.com/maps/basic-v2/style.json?key={key}',
    attribution: '© MapTiler © OpenStreetMap contributors',
    note: 'POI 更少,图钉最突出',
    needsKey: true,
  },
  {
    name: '极简灰',
    url: 'https://api.maptiler.com/maps/dataviz/style.json?key={key}',
    attribution: '© MapTiler © OpenStreetMap contributors',
    note: '几乎只有路网 —— 单量大、图钉密的时候用这个',
    needsKey: true,
  },
  {
    name: 'Thunderforest',
    url: 'https://tile.thunderforest.com/transport/{z}/{x}/{y}.png?apikey={key}',
    attribution: '© Thunderforest © OpenStreetMap contributors',
    note: '栅格瓦片,交通图层清楚',
    needsKey: true,
  },
  {
    name: 'Mapbox',
    url: 'https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/{z}/{x}/{y}?access_token={key}',
    attribution: '© Mapbox © OpenStreetMap contributors',
    note: '按加载次数计费,注意用量',
    needsKey: true,
  },
  {
    name: '自建',
    url: 'https://tiles.example.com/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors',
    note: '自己搭一套,量大时最省',
    needsKey: false,
  },
];

export function MapSettings({ canManage }: { canManage: boolean }) {
  const [cfg, setCfg] = useState<Cfg>({ tileUrl: null, keyRef: null, attribution: null });
  const [tileUrl, setTileUrl] = useState('');
  const [keyRef, setKeyRef] = useState('');
  const [attribution, setAttribution] = useState('');
  const [keyMissing, setKeyMissing] = useState(false);
  /*
   * ★ 人动过表单没有。
   *
   *   这一屏进来时会去 /auth/me 读一次配置回填 —— 而那一次请求
   *   可能在人已经开始打字之后才回来,回来就把他填的清掉了。
   *   表现是「我明明填了,一保存却是空的」,而且只在网慢的时候出现。
   *   (取货点表单踩过同一个坑,这里是第二次。)
   */
  const dirtyRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void api<{
      tenant: {
        mapTileUrl?: string | null;
        mapKeyRef?: string | null;
        mapAttribution?: string | null;
        tiles?: { keyMissing: boolean } | null;
      };
    }>('/auth/me')
      .then((me) => {
        // ★ 人已经在填了就别回填 —— 回来晚的那次会把他填的清掉
        if (dirtyRef.current) return;
        const c = {
          tileUrl: me.tenant.mapTileUrl ?? null,
          keyRef: me.tenant.mapKeyRef ?? null,
          attribution: me.tenant.mapAttribution ?? null,
        };
        setCfg(c);
        setTileUrl(c.tileUrl ?? '');
        setKeyRef(c.keyRef ?? '');
        setAttribution(c.attribution ?? '');
        setKeyMissing(me.tenant.tiles?.keyMissing ?? false);
      })
      .catch(() => undefined);
  }, []);

  async function save(next: Cfg) {
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      await api('/settings/map', { method: 'PATCH', body: next });
      setCfg(next);
      setSaved(true);
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-label="地图底图">
      <h2 className="mb-1 text-xl font-semibold text-slate-900 dark:text-slate-100">地图底图</h2>
      <p className="mb-4 text-sm text-slate-500">
        {cfg.tileUrl
          ? '已配置 —— 管理端和司机端都会用这一份'
          : '没配置 —— 现在用的是 OpenStreetMap 公共瓦片,它的使用政策禁止商业/高流量使用,上线前必须换掉'}
      </p>

      <div className="max-w-2xl space-y-3 rounded-xl border border-slate-200 p-4 dark:border-slate-700">
        {/*
          ★ 这一条要显眼。人配 key 时的默认心智是「存在服务器上就是安全的」,
            而瓦片这里不是 —— 地址是浏览器拿去拉图的,key 必然可见。
        */}
        <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800
                        dark:bg-amber-950 dark:text-amber-300">
          底图由**浏览器**直接去拉,所以地址里的 key 一定会出现在客户端 ——
          所有瓦片服务都是这样。防盗用要在供应商后台按 HTTP Referer 限制来源,
          不要指望把 key 藏起来。
        </div>

        {/*
          ★ 给几个现成的选项,而不是让人往里粘 URL。
            「底图地址」这一栏对配的人来说是个空白题:他要先去供应商网站
            找样式名、拼参数、再猜 {key} 该放哪。而实际上九成的人
            只想要「像 Google 那样、别太花」。
            粘 URL 的路留着 —— 自建服务和别的供应商还得靠它。
        */}
        <div>
          <div className="text-sm text-slate-500">常用底图</div>
          <div className="mt-1 flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.name}
                type="button"
                disabled={!canManage}
                onClick={() => {
                  dirtyRef.current = true;
                  setTileUrl(p.url);
                  setKeyRef(p.needsKey ? 'env:MAP_TILE_KEY' : '');
                  setAttribution(p.attribution);
                }}
                className={`rounded-lg border px-3 py-1.5 text-sm disabled:opacity-40 ${
                  tileUrl === p.url
                    ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-500/10'
                    : 'border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-300'
                }`}
                title={p.note}
              >
                {p.name}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-slate-400">
            选完还要保存。带密钥的那几个会填上 <code>env:MAP_TILE_KEY</code> —— 
            服务器上得有这个环境变量
          </p>
        </div>

        <label className="block text-sm">
          <span className="text-slate-500">底图地址</span>
          <span className="block text-xs text-slate-400">
            栅格瓦片写 <code>{'{z}/{x}/{y}'}</code> 模板,矢量样式直接填 style.json 的地址;
            <code>{'{key}'}</code> 会被替换成密钥
          </span>
          <input
            aria-label="底图地址"
            value={tileUrl}
            disabled={!canManage}
            onChange={(e) => {
              dirtyRef.current = true;
              setTileUrl(e.target.value);
            }}
            placeholder="https://api.maptiler.com/maps/streets-v2/style.json?key={key}"
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5
                       font-mono text-xs dark:border-slate-600 dark:bg-slate-800"
          />
        </label>

        <label className="block text-sm">
          <span className="text-slate-500">密钥引用</span>
          <span className="block text-xs text-slate-400">
            填 <code>env:变量名</code> —— 密钥放在服务器的环境变量里,不进数据库、不跟着备份走
          </span>
          <input
            aria-label="底图密钥引用"
            value={keyRef}
            disabled={!canManage}
            onChange={(e) => {
              dirtyRef.current = true;
              setKeyRef(e.target.value);
            }}
            placeholder="env:MAP_TILE_KEY"
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5
                       font-mono text-xs dark:border-slate-600 dark:bg-slate-800"
          />
        </label>

        {/*
          ★ 「地址里要 key,而那个环境变量没设」要当场说。
            不说的话表现是地图一片空白 —— 看起来像地图坏了,
            没人会想到是某个环境变量没设。
        */}
        {keyMissing && (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700
                          dark:bg-red-950 dark:text-red-400">
            地址里有 {'{key}'} 占位符,但服务器上取不到 {cfg.keyRef} 这个环境变量 ——
            瓦片会请求失败,地图一片空白
          </div>
        )}

        <label className="block text-sm">
          <span className="text-slate-500">版权署名</span>
          <span className="block text-xs text-slate-400">
            供应商大多在服务条款里要求显示,不填用默认的 OpenStreetMap 署名
          </span>
          <input
            aria-label="版权署名"
            value={attribution}
            disabled={!canManage}
            onChange={(e) => {
              dirtyRef.current = true;
              setAttribution(e.target.value);
            }}
            placeholder="© MapTiler © OpenStreetMap contributors"
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5
                       text-xs dark:border-slate-600 dark:bg-slate-800"
          />
        </label>

        {canManage && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() =>
                void save({
                  tileUrl: tileUrl.trim() || null,
                  keyRef: keyRef.trim() || null,
                  attribution: attribution.trim() || null,
                })
              }
              disabled={busy}
              className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white
                         disabled:opacity-40"
            >
              {busy ? '保存中…' : '保存'}
            </button>
            {cfg.tileUrl && (
              <button
                onClick={() => {
                  setTileUrl('');
                  setKeyRef('');
                  setAttribution('');
                  void save({ tileUrl: null, keyRef: null, attribution: null });
                }}
                disabled={busy}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600
                           dark:border-slate-600 dark:text-slate-400"
              >
                清除
              </button>
            )}
            {saved && (
              <span className="text-sm text-green-600 dark:text-green-400">
                已保存 —— 刷新页面后生效
              </span>
            )}
          </div>
        )}

        {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}

        {canManage && (
          <div className="border-t border-slate-100 pt-3 dark:border-slate-700/60">
            <div className="mb-1 text-xs text-slate-400">常见供应商(点一下填进去,再按需要改)</div>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.name}
                  onClick={() => {
                    setTileUrl(p.url);
                    setAttribution(p.attribution);
                  }}
                  title={p.note}
                  className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs text-slate-600
                             dark:border-slate-600 dark:text-slate-400"
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
